import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Shield, AlertTriangle, CheckCircle, XCircle, Globe } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

const INDICATOR_CONFIG = {
  Critical: { bg: 'bg-red-50',    border: 'border-red-400',    text: 'text-red-700',    badge: 'bg-red-100 text-red-700 border-red-300',    icon: XCircle       },
  High:     { bg: 'bg-orange-50', border: 'border-orange-400', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertTriangle },
  Moderate: { bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: AlertTriangle },
  Low:      { bg: 'bg-green-50',  border: 'border-green-300',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700 border-green-300',  icon: CheckCircle  },
};

const StatTile = ({ label, value, sub, color = 'text-gray-800' }) => (
  <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
    <div className={`text-2xl font-bold tabular-nums ${color}`}>{value}</div>
    <div className="text-xs font-medium text-gray-600 mt-0.5">{label}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

export default function RansomwarePreScanPanel() {
  const apiCall = useApi();
  const [scan,    setScan]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const runScan = useCallback(async () => {
    // Guard: only run if a valid org is selected
    const org = (() => { try { return JSON.parse(localStorage.getItem('selectedOrganization')); } catch { return null; } })();
    if (!org?.organizationId || org.organizationId <= 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall('GET', '/ransomware/intelligence-scan');
      setScan(data);
    } catch (e) {
      setError('Scan failed — check your connection or try again.');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Auto-run on mount
  useEffect(() => { runScan(); }, [runScan]);

  const cfg     = scan ? (INDICATOR_CONFIG[scan.riskIndicator] ?? INDICATOR_CONFIG.Low) : null;
  const IndicatorIcon = cfg?.icon ?? Shield;

  return (
    <div className={`border rounded-xl mb-6 overflow-hidden shadow-sm ${cfg ? `${cfg.border} ${cfg.bg}` : 'border-gray-200 bg-white'}`}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/50">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <div>
            <span className="text-sm font-semibold text-gray-900">Pre-Assessment Intelligence Scan</span>
            <p className="text-xs text-gray-500 mt-0.5">
              Dark web + threat signals — snapshot before starting an assessment
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {scan?.domain && (
            <span className="flex items-center gap-1 text-xs text-gray-500 bg-white/70 border border-gray-200 rounded px-2 py-1">
              <Globe className="h-3 w-3" />
              {scan.domain}
            </span>
          )}
          <button
            onClick={runScan}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Scanning…' : 'Rescan'}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && !scan && (
        <div className="px-5 py-8 text-center">
          <RefreshCw className="h-6 w-6 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500">Scanning dark web and threat intelligence sources…</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="px-5 py-4 text-sm text-red-600">{error}</div>
      )}

      {/* Results */}
      {scan && !loading && (
        <div className="px-5 py-4">

          {/* Risk indicator banner */}
          <div className="flex items-center gap-3 mb-4">
            <span className={`flex items-center gap-1.5 text-xs font-semibold border rounded-full px-3 py-1 ${cfg.badge}`}>
              <IndicatorIcon className="h-3.5 w-3.5" />
              {scan.riskIndicator} Pre-Assessment Risk
            </span>
            <span className="text-xs text-gray-400">
              Last {scan.windowDays}-day window · {fmtDate(scan.scannedAtUtc)}
            </span>
            {scan.isPartial && (
              <span className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded px-2 py-0.5">
                Partial — one source unavailable
              </span>
            )}
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatTile
              label="Credential Exposures"
              value={scan.credentialExposures90d}
              sub={scan.uniqueSourcesCount > 0 ? `${scan.uniqueSourcesCount} source(s)` : '90-day window'}
              color={scan.credentialExposures90d >= 6 ? 'text-red-600' : scan.credentialExposures90d > 0 ? 'text-orange-600' : 'text-green-600'}
            />
            <StatTile
              label="Telegram — High"
              value={scan.telegramHighSignals90d}
              sub="high-severity signals"
              color={scan.telegramHighSignals90d >= 6 ? 'text-orange-600' : scan.telegramHighSignals90d > 0 ? 'text-yellow-600' : 'text-gray-400'}
            />
            <StatTile
              label="Telegram — Critical"
              value={scan.telegramCriticalSignals90d}
              sub="critical-severity signals"
              color={scan.telegramCriticalSignals90d > 0 ? 'text-red-600' : 'text-gray-400'}
            />
            <StatTile
              label="Incidents Raised"
              value={scan.telegramIncidentsTotal}
              sub="all-time"
              color={scan.telegramIncidentsTotal > 0 ? 'text-orange-600' : 'text-gray-400'}
            />
          </div>

          {/* Risk reasons */}
          <div className="space-y-1 mb-4">
            {scan.riskReasons.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-gray-700">
                <span className="mt-0.5 flex-shrink-0">
                  {scan.riskIndicator === 'Low' ? '✓' : '⚠'}
                </span>
                {r}
              </div>
            ))}
          </div>

          {/* Recent credential exposures */}
          {scan.recentExposures.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Recent credential exposures
              </div>
              <div className="space-y-1">
                {scan.recentExposures.map((e, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs bg-white/60 rounded px-2 py-1.5 border border-white/80">
                    <span className="font-medium text-gray-800 truncate max-w-40">{e.username}</span>
                    {e.source && <span className="text-gray-400 truncate">{e.source}</span>}
                    {e.numCredentials > 0 && (
                      <span className="text-red-600 font-medium ml-auto flex-shrink-0">{e.numCredentials} cred{e.numCredentials !== 1 ? 's' : ''}</span>
                    )}
                    {e.exposedAt && <span className="text-gray-400 flex-shrink-0">{fmtDate(e.exposedAt)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Telegram signals */}
          {scan.recentTelegramSignals.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Recent threat signals
              </div>
              <div className="space-y-1">
                {scan.recentTelegramSignals.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs bg-white/60 rounded px-2 py-1.5 border border-white/80">
                    <span className={`flex-shrink-0 font-medium mt-0.5 ${s.severity === 'Critical' ? 'text-red-600' : 'text-orange-600'}`}>
                      {s.severity}
                    </span>
                    <span className="text-gray-500 flex-shrink-0">{s.channelName}</span>
                    <span className="text-gray-700 line-clamp-1 flex-1">{s.snippet}</span>
                    <span className="text-gray-400 flex-shrink-0">{fmtDate(s.messageTimestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No signal message */}
          {!scan.hasDarkWebData && !scan.hasTelegramData && (
            <p className="text-xs text-gray-400 italic">
              No threat intelligence sources are configured for this organization.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
